import { useQuery } from '@tanstack/react-query';
import { useCustomerScope } from '../contexts/AccountContext';
import { supabase } from '../lib/supabase';

export interface CustomerLocationService {
  id: string;
  code: string | null;
  type: string;
  provider: string | null;
  circuit_id: string | null;
  bandwidth_down_mbps: number | null;
  bandwidth_up_mbps: number | null;
  bandwidth_label: string | null;
  transport: string | null;
  handoff: string | null;
  sla_target: string | null;
  mttr_hours: number | null;
  status: string;
  updated_at: string;
}
export interface CustomerLocationVisit {
  id: string;
  visited_at: string;
  technician_name: string | null;
  work_performed: string | null;
}

/** Explicit columns and current account scope also constrain staff view-as. */
export function useLocationServices(locationId: string) {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['location-services', scope.key, locationId],
    enabled: scope.ready && scope.ids.length > 0 && Boolean(locationId),
    queryFn: async (): Promise<CustomerLocationService[]> => {
      const { data, error } = await supabase
        .from('location_services_customer_view')
        .select(
          'id,code,type,provider,circuit_id,bandwidth_down_mbps,bandwidth_up_mbps,bandwidth_label,transport,handoff,sla_target,mttr_hours,status,updated_at',
        )
        .eq('location_id', locationId)
        .in('customer_id', scope.ids)
        .order('code');
      if (error) throw error;
      return (data ?? []) as CustomerLocationService[];
    },
    staleTime: 60_000,
  });
}

export function useLocationVisitHistory(locationId: string) {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['location-visit-history', scope.key, locationId],
    enabled: scope.ready && scope.ids.length > 0 && Boolean(locationId),
    queryFn: async (): Promise<CustomerLocationVisit[]> => {
      const { data, error } = await supabase
        .from('location_visit_history_customer_view')
        .select('id,visited_at,technician_name,work_performed')
        .eq('location_id', locationId)
        .in('customer_id', scope.ids)
        .order('visited_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as CustomerLocationVisit[];
    },
    staleTime: 60_000,
  });
}
